import {
  DentistInfo,
  getDentistIdForSplitScheduling,
  getOperatoryId,
} from 'pages/UpdatedSearchResultPage/utils';
import {
  FamilyTimeSlot,
  FamilyTimeSlotBlock,
  FamilyTimeSlotBlockService,
} from 'interfaces/familyBookingTypes';
import getChairOptimizationForExamCleaning from 'utils/getChairOptimizationForExamCleaning';
import {
  MINUTE_OF_A_BLOCK,
  convertTimeHHmmss,
} from 'utils/convertMinutesIntoHoursOfDay';
import { findBestOperatory } from 'utils/findOperatory';
import { IChairOptimization } from 'interfaces/createBookingDataTypes';

interface IBuildSlotParams {
  slot: FamilyTimeSlot;
  block: FamilyTimeSlotBlock;
  service: FamilyTimeSlotBlockService;
  date: string;
  preferredDentistId: string | null;
  isExamCleaningService: boolean;
}

interface ISlotPlacement {
  operatoryId: string;
  dentist: DentistInfo;
  chairOptimizations: IChairOptimization[] | null;
}

const buildNormalServicePlacement = ({
  block,
  service,
}: Pick<IBuildSlotParams, 'block' | 'service'>): ISlotPlacement => {
  const bestOperatory = findBestOperatory({
    selectedSlot: block.value,
    availableBlockOperatories: service.availableBlockOperatories || [],
    serviceDuration: service.searchingService.duration,
  });

  return {
    operatoryId: bestOperatory?.operatoryId || '',
    dentist: {
      id: null,
      timeSlot: block.value,
      timeSlotType: null,
    },
    chairOptimizations: null,
  };
};

const buildExamAndCleaningPlacement = ({
  slot,
  block,
  service,
  date,
  preferredDentistId,
}: IBuildSlotParams): ISlotPlacement => {
  const doctors = [
    {
      id: slot.doctor.id,
      availableBlocks: [
        {
          date,
          availableBlockOperatories: service.availableBlockOperatories,
          dentists: service.dentists,
          searchingService: service.searchingService,
        },
      ],
    },
  ];

  return {
    operatoryId: getOperatoryId({
      date,
      doctorId: slot.doctor.id,
      timeSlot: block.value,
      doctors,
    }),
    dentist: getDentistIdForSplitScheduling({
      date,
      doctorId: slot.doctor.id,
      selectedDentistId: preferredDentistId,
      timeSlot: block.value,
      doctors,
    }),
    //--Only the practitioner the patient picked: the backend resolves the chair against that doctor alone--
    chairOptimizations: [
      getChairOptimizationForExamCleaning({
        id: slot.doctor.id,
        availableBlock: {
          date,
          availableBlockOperatories: service.availableBlockOperatories,
          blocks: [block.value],
        },
        selectedSlot: block.value,
      }),
    ],
  };
};

export const buildAppointmentFromSlot = (params: IBuildSlotParams) => {
  const { slot, block, service, date, isExamCleaningService } = params;

  const { operatoryId, dentist, chairOptimizations } = isExamCleaningService
    ? buildExamAndCleaningPlacement(params)
    : buildNormalServicePlacement(params);

  return {
    doctorId: slot.doctor.id,
    practitionerName: slot.doctor.name,
    startTime: convertTimeHHmmss(block.value * MINUTE_OF_A_BLOCK),
    appointmentDate: date,
    operatoryId,
    dentist,
    childServiceId: service.childServiceId,
    chairOptimizations,
  };
};
